import { useState, useEffect } from "react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { 
  Video, Shield, UserCheck, Clock, CheckCircle2, 
  TrendingUp, Zap, Building2, Users, Award,
  ArrowRight, Star, Globe, Lock, FileCheck,
  Briefcase, BarChart3, Target, Sparkles,
  BadgeCheck, GraduationCap, Landmark, Monitor, Menu, X,
  Stethoscope, Building, Inbox, AlertTriangle
} from "lucide-react";
import vexisLogo from "@assets/vexis-logo-2026.jpg";
import heroImage from "@assets/hero-hiring-ops.webp";
import hiringPressureImage from "@assets/hiring-pressure.webp";
import executiveTeamImage from "@assets/executive-team.webp";
import teamCollaborationImage from "@assets/team-collaboration.webp";
import candidateAssessmentImage from "@assets/candidate-assessment.webp";
import ctaBannerImage from "@assets/vexis-cta-banner.webp";

const INDIGO = "rgba(99,102,241,";
const cardClass = "rounded-2xl border border-[rgba(99,102,241,0.18)] bg-white/[0.03] hover:border-[rgba(99,102,241,0.45)] hover:bg-white/[0.06] transition-all duration-300";
const sectionSep = "h-px bg-gradient-to-r from-transparent via-[rgba(99,102,241,0.25)] to-transparent";

export default function Home() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    document.title = "Vexis — Human-First Recruitment Assessment";
  }, []);

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#05070a", color: "#ffffff", fontFamily: "'Inter', 'Plus Jakarta Sans', system-ui, sans-serif" }}>

      {/* Navigation */}
      <nav style={{ backgroundColor: "rgba(5,7,10,0.92)", borderBottom: "1px solid rgba(99,102,241,0.15)", backdropFilter: "blur(20px)" }} className="sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link href="/" className="flex items-center gap-3">
            <img src={vexisLogo} alt="VEXIS Talent Agency" className="h-10 md:h-12 w-auto" />
            <span className="text-white font-bold tracking-widest text-sm">VEXIS</span>
          </Link>
          <div className="hidden lg:flex items-center gap-8">
            <a href="#process" className="text-sm font-medium transition-colors" style={{ color: "#94a3b8" }}
              onMouseEnter={e => (e.currentTarget.style.color = "#ffffff")}
              onMouseLeave={e => (e.currentTarget.style.color = "#94a3b8")}>How It Works</a>
            <Link href="/pricing" className="text-sm font-medium transition-colors" style={{ color: "#94a3b8" }}
              onMouseEnter={e => (e.currentTarget.style.color = "#ffffff")}
              onMouseLeave={e => (e.currentTarget.style.color = "#94a3b8")}>Pricing</Link>
            <Link href="/about" className="text-sm font-medium transition-colors" style={{ color: "#94a3b8" }}>About Vexis</Link>
            <Link href="/partners" className="text-sm font-medium transition-colors" style={{ color: "#94a3b8" }}>Partners</Link>
          </div>
          <div className="hidden lg:flex items-center gap-3">
            <Link href="/ops" data-testid="link-ops-access">
              <span className="text-xs font-medium px-2 py-1 rounded transition-colors cursor-pointer"
                style={{ color: "rgba(148,163,184,0.5)" }}
                onMouseEnter={e => ((e.currentTarget as HTMLElement).style.color = "#94a3b8")}
                onMouseLeave={e => ((e.currentTarget as HTMLElement).style.color = "rgba(148,163,184,0.5)")}>
                Operations
              </span>
            </Link>
            <Link href="/login">
              <Button variant="ghost" className="font-medium text-slate-300 hover:text-white hover:bg-white/10" data-testid="button-portal-access">
                Sign In
              </Button>
            </Link>
            <Link href="/contact">
              <Button className="font-semibold" style={{ backgroundColor: "#6366f1", color: "#ffffff" }}
                onMouseEnter={e => ((e.currentTarget as HTMLElement).style.backgroundColor = "#5558e3")}
                onMouseLeave={e => ((e.currentTarget as HTMLElement).style.backgroundColor = "#6366f1")}
                data-testid="button-get-started">
                Submit Enquiry
              </Button>
            </Link>
          </div>
          <button
            className="lg:hidden p-2"
            style={{ color: "#ffffff" }}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div style={{ backgroundColor: "#05070a", borderTop: "1px solid rgba(99,102,241,0.15)" }}>
            <div className="container mx-auto px-4 py-4 flex flex-col gap-4">
              <a href="#process" className="text-sm font-medium py-2" style={{ color: "#94a3b8" }} onClick={() => setMobileMenuOpen(false)}>How It Works</a>
              <Link href="/pricing" className="text-sm font-medium py-2" style={{ color: "#94a3b8" }} onClick={() => setMobileMenuOpen(false)}>Pricing</Link>
              <Link href="/about" className="text-sm font-medium py-2" style={{ color: "#94a3b8" }} onClick={() => setMobileMenuOpen(false)}>About Vexis</Link>
              <Link href="/partners" className="text-sm font-medium py-2" style={{ color: "#94a3b8" }} onClick={() => setMobileMenuOpen(false)}>Partners</Link>
              <Link href="/contact" className="text-sm font-medium py-2" style={{ color: "#94a3b8" }} onClick={() => setMobileMenuOpen(false)}>Contact</Link>
              <div className="flex flex-col gap-2 pt-4" style={{ borderTop: "1px solid rgba(99,102,241,0.15)" }}>
                <Link href="/login" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="outline" className="w-full font-medium border-white/20 text-white hover:bg-white/10">Sign In</Button>
                </Link>
                <Link href="/contact" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="w-full font-semibold" style={{ backgroundColor: "#6366f1", color: "#ffffff" }}>Submit Enquiry</Button>
                </Link>
                <Link href="/ops" onClick={() => setMobileMenuOpen(false)} data-testid="link-ops-access-mobile">
                  <span className="block w-full text-center text-xs py-1 transition-colors" style={{ color: "rgba(148,163,184,0.4)" }}>
                    Operations
                  </span>
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative flex items-center" style={{
        backgroundColor: "#0a0f1e",
        minHeight: "90vh",
        paddingTop: "80px",
        paddingBottom: "80px",
        overflow: "clip",
      }}>
        {/* Vexis hero backdrop */}
        <div className="absolute inset-0 pointer-events-none" style={{
          backgroundImage: "url('/images/vexis-hero-new.jpg')",
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center center",
          opacity: 0.42,
        }} />
        {/* Dark overlay — left heavy so text stays readable, lightens toward center */}
        <div className="absolute inset-0 pointer-events-none" style={{
          background: "linear-gradient(to right, rgba(5,7,10,0.82) 0%, rgba(5,7,10,0.48) 42%, rgba(5,7,10,0.62) 100%)",
        }} />
        {/* Subtle top/bottom fade to blend into page */}
        <div className="absolute inset-0 pointer-events-none" style={{
          background: "linear-gradient(to bottom, rgba(5,7,10,0.45) 0%, transparent 18%, transparent 82%, rgba(5,7,10,0.55) 100%)",
        }} />

        <div className="container mx-auto px-4 relative z-10 w-full">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-8">

            {/* LEFT: Text column */}
            <div className="flex-1 lg:max-w-[55%] w-full text-center lg:text-left">

              {/* Eyebrow */}
              <p className="hero-anim-0 text-xs font-semibold uppercase tracking-widest mb-4" style={{ color: "#3B82F6" }}
                data-testid="hero-eyebrow">
                The Hybrid Recruitment Platform
              </p>

              {/* Headline */}
              <h1 className="hero-anim-1 font-bold leading-[1.1] mb-5" style={{
                color: "#ffffff",
                fontSize: "clamp(2rem, 5vw, 3.5rem)",
              }} data-testid="hero-headline">
                Great hires start with a{" "}
                <span style={{ color: "#3B82F6" }}>real</span>
                {" "}conversation.
              </h1>

              {/* Sub-headline */}
              <p className="hero-anim-2 leading-relaxed mb-8 mx-auto lg:mx-0" style={{
                color: "#94A3B8",
                fontSize: "clamp(1rem, 2vw, 1.125rem)",
                maxWidth: "480px",
                lineHeight: "1.6",
              }} data-testid="hero-subheadline">
                Resumes tell you what someone has done. Vexis shows you who they are.<br /><br />
                Structured video assessments. AI-assisted analysis. Your expert judgement makes the final call.<br /><br />
                Built for recruiters who know that the right person is worth finding.
              </p>

              {/* CTAs */}
              <div className="hero-anim-3 flex flex-col sm:flex-row gap-3 justify-center lg:justify-start mb-8">
                <Link href="/register">
                  <Button size="lg" className="w-full sm:w-auto font-semibold px-7 h-12"
                    style={{ backgroundColor: "#3B82F6", color: "#ffffff" }}
                    data-testid="button-start-trial">
                    Start Assessing Candidates →
                  </Button>
                </Link>
                <a href="https://app.supademo.com/demo/cmo86lr5n0qd9l2dysboksx6t?utm_source=link" target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto">
                  <Button size="lg" variant="outline" className="w-full h-12 px-7"
                    style={{ borderColor: "rgba(255,255,255,0.25)", color: "#ffffff", backgroundColor: "transparent" }}
                    data-testid="button-see-how-it-works">
                    See It In Action
                  </Button>
                </a>
              </div>

              {/* Trust indicators */}
              <div className="hero-anim-4 flex flex-wrap justify-center lg:justify-start gap-x-5 gap-y-2">
                {[
                  "Every outcome reviewed by a human",
                  "Seamless JobAdder integration",
                  "Candidates assessed in under 15 minutes",
                ].map((item) => (
                  <div key={item} className="flex items-center gap-1.5" style={{ color: "#94A3B8" }}>
                    <CheckCircle2 className="w-3.5 h-3.5 shrink-0" style={{ color: "#22C55E" }} />
                    <span className="text-xs">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* RIGHT: Phone mockup column */}
            <div className="flex-1 lg:max-w-[45%] flex justify-center lg:justify-end" style={{ paddingRight: "32px", paddingBottom: "24px" }}>
              <div className="relative hero-phone" style={{ display: "inline-block" }}>

                {/* Phone frame */}
                <div className="hero-phone-frame" style={{
                  border: "3px solid rgba(255,255,255,0.15)",
                  borderRadius: "40px",
                  background: "#0a0a1a",
                  padding: "12px",
                  boxShadow: "0 0 0 1px rgba(255,255,255,0.05), 0 40px 80px rgba(0,0,0,0.6), 0 0 60px rgba(59,130,246,0.15)",
                  width: "100%",
                }}>
                  {/* Notch */}
                  <div style={{
                    width: "80px",
                    height: "6px",
                    background: "rgba(255,255,255,0.1)",
                    borderRadius: "3px",
                    margin: "0 auto 8px auto",
                  }} />

                  {/* Screenshot */}
                  <img
                    src="/images/candidate-interview.webp"
                    alt="Vexis candidate video interview on mobile"
                    width={256}
                    height={554}
                    fetchPriority="high"
                    decoding="async"
                    style={{
                      width: "100%",
                      borderRadius: "28px",
                      display: "block",
                    }}
                    data-testid="hero-phone-screenshot"
                  />
                </div>

                {/* Badge: Camera/Mic Ready (bottom-left) */}
                <div className="hero-badge hero-badge-bl" style={{
                  position: "absolute",
                  bottom: "-16px",
                  left: "-16px",
                  background: "#ffffff",
                  borderRadius: "12px",
                  padding: "10px 14px",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
                  minWidth: "130px",
                }}>
                  <div className="flex items-center gap-2 mb-0.5">
                    <span style={{
                      width: "8px",
                      height: "8px",
                      borderRadius: "50%",
                      background: "#22C55E",
                      display: "inline-block",
                      animation: "pulse 2s infinite",
                      flexShrink: 0,
                    }} />
                    <span style={{ fontSize: "11px", fontWeight: 600, color: "#374151" }}>Camera Ready</span>
                  </div>
                  <div style={{ fontSize: "11px", color: "#6B7280", paddingLeft: "16px" }}>Microphone Ready</div>
                </div>

                {/* Badge: Question progress (top-right) */}
                <div className="hero-badge hero-badge-tr" style={{
                  position: "absolute",
                  top: "20px",
                  right: "-20px",
                  background: "#1e40af",
                  borderRadius: "10px",
                  padding: "8px 12px",
                  color: "#ffffff",
                  fontSize: "11px",
                  fontWeight: 700,
                  boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
                  whiteSpace: "nowrap",
                }}>
                  Question 1 of 6
                </div>

              </div>
            </div>

          </div>
        </div>
      </section>

      <div className={sectionSep} />

      {/* Stats Bar */}
      <section className="py-6 md:py-10" style={{ backgroundColor: "#080b12" }}>
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-12 max-w-5xl mx-auto">
            {[
              { label: "Execution Layer", value: "Operational" },
              { label: "Assessment Signals", value: "Standardised" },
              { label: "Quality Inputs", value: "Decision" },
              { label: "Output Format", value: "Dossier-Ready" },
            ].map(({ label, value }, i) => (
              <div key={label} className="text-center px-2 md:px-4" style={{ borderRight: i < 3 ? "1px solid rgba(99,102,241,0.15)" : "none" }}>
                <div className="text-lg md:text-3xl font-bold tracking-tight" style={{ color: "#ffffff" }}>{value}</div>
                <div className="text-[10px] md:text-sm mt-1 md:mt-2 uppercase tracking-wider" style={{ color: "#64748b" }}>{label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Official Integration Partner Strip */}
      <section className="py-5 md:py-6" style={{ backgroundColor: "#060910", borderTop: "1px solid rgba(99,102,241,0.12)", borderBottom: "1px solid rgba(99,102,241,0.12)" }}>
        <div className="container mx-auto px-4">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6">
            <span className="text-[10px] uppercase tracking-[0.18em] font-semibold" style={{ color: "#475569" }}>Official ATS Integration Partner</span>
            <div className="hidden sm:block h-4 w-px" style={{ backgroundColor: "rgba(99,102,241,0.3)" }} />
            <div className="flex items-center gap-4">
              <img
                src="/images/jobadder-logo.jpg"
                alt="JobAdder"
                className="h-16 w-16 rounded-2xl object-cover"
                style={{ boxShadow: "0 0 0 2px rgba(59,130,246,0.5), 0 4px 20px rgba(59,130,246,0.18)" }}
              />
              <div className="flex flex-col">
                <span className="text-base font-bold" style={{ color: "#ffffff" }}>JobAdder</span>
                <span className="text-[11px]" style={{ color: "#64748b" }}>Official ATS Partner</span>
              </div>
            </div>
            <div className="hidden sm:block h-4 w-px" style={{ backgroundColor: "rgba(99,102,241,0.3)" }} />
            <span className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "#334155" }}>More integrations coming soon</span>
          </div>
        </div>
      </section>

      <div className={sectionSep} />

      {/* When Hiring Breaks Down */}
      <section className="py-16 md:py-28" style={{ backgroundColor: "#05070a", position: "relative" }}>
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 50% 50% at 80% 30%, rgba(55,48,163,0.12) 0%, transparent 70%)" }} />
        <div className="container mx-auto px-4 relative">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center mb-12 md:mb-20">
              <div>
                <p className="uppercase tracking-widest text-xs mb-3 font-semibold" style={{ color: "#818cf8" }}>Why Vexis</p>
                <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{
                  background: "linear-gradient(to bottom, #ffffff 0%, #c7d2fe 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text"
                }}>
                  When Hiring Breaks Down
                </h2>
                <p className="text-base md:text-lg leading-relaxed" style={{ color: "#94a3b8" }}>
                  Internal teams lose momentum when hiring volume exceeds capacity. Decisions slow, quality drops, and the best candidates move on.
                </p>
              </div>

              {/* Image — framed with indigo glow */}
              <div className="relative rounded-2xl overflow-hidden" style={{
                boxShadow: "0 0 0 1px rgba(99,102,241,0.3), 0 0 60px rgba(99,102,241,0.12)",
              }}>
                <img
                  src={hiringPressureImage}
                  alt="Vexis talent pipeline kanban board showing candidate tracking across workflow stages"
                  className="w-full h-64 lg:h-80 object-cover block"
                  loading="lazy"
                  decoding="async"
                  data-testid="img-hiring-pressure"
                />
                <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(5,7,10,0.6) 0%, rgba(5,7,10,0.1) 60%, transparent 100%)" }} />
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-5 mb-12">
              {[
                { icon: Inbox, color: "#f87171", bg: "rgba(248,113,113,0.1)", title: "Application Overload", desc: "High application volumes overwhelm recruitment agencies and talent acquisition teams, slowing decisions and extending time-to-hire beyond competitive thresholds." },
                { icon: Clock, color: "#fbbf24", bg: "rgba(251,191,36,0.1)", title: "Low-Signal Review", desc: "Recruitment agencies and talent acquisition teams lose time to manual, unstructured review processes that produce inconsistent candidate signals and poor decision inputs." },
                { icon: AlertTriangle, color: "#fb923c", bg: "rgba(251,146,60,0.1)", title: "Operational Strain", desc: "Hiring surges create unsustainable operational strain, diverting resources from core business functions and increasing risk." },
              ].map(({ icon: Icon, color, bg, title, desc }) => (
                <div key={title} className={`p-7 ${cardClass}`}>
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={{ backgroundColor: bg }}>
                    <Icon className="w-6 h-6" style={{ color }} />
                  </div>
                  <h3 className="text-lg font-bold mb-3" style={{ color: "#ffffff" }}>{title}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: "#94a3b8" }}>{desc}</p>
                </div>
              ))}
            </div>

            <div className="text-center p-6 rounded-2xl" style={{ backgroundColor: "rgba(99,102,241,0.08)", border: "1px solid rgba(99,102,241,0.25)" }}>
              <p className="text-lg font-medium max-w-3xl mx-auto" style={{ color: "#e2e8f0" }}>
                Vexis solves this by standardising early-stage evaluation and producing decision-ready outputs — adding a structured assessment layer to your hiring workflow.
              </p>
            </div>
          </div>
        </div>
      </section>

      <div className={sectionSep} />

      {/* Industry Sectors — Bento Grid */}
      <section id="industries" className="py-16 md:py-28" style={{ backgroundColor: "#080b12", position: "relative" }}>
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 60% 50% at 20% 80%, rgba(55,48,163,0.14) 0%, transparent 70%)" }} />
        <div className="container mx-auto px-4 relative">
          <div className="max-w-6xl mx-auto">

            <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center mb-12 md:mb-20">
              <div className="order-2 lg:order-1 relative rounded-2xl overflow-hidden" style={{
                boxShadow: "0 0 0 1px rgba(99,102,241,0.3), 0 0 80px rgba(99,102,241,0.10)",
              }}>
                <img
                  src={executiveTeamImage}
                  alt="Vexis candidate dossier profile view with assessment scores and capability signals"
                  className="w-full h-64 lg:h-80 object-cover block"
                  loading="lazy"
                  decoding="async"
                  data-testid="img-executive-team"
                />
                <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(8,11,18,0.55) 0%, transparent 60%)" }} />
              </div>
              <div className="order-1 lg:order-2">
                <p className="uppercase tracking-widest text-xs mb-3 font-semibold" style={{ color: "#818cf8" }}>Specialised Sectors</p>
                <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{
                  background: "linear-gradient(to bottom, #ffffff 0%, #c7d2fe 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text"
                }}>
                  Industry Expertise
                </h2>
                <p className="text-base md:text-lg leading-relaxed" style={{ color: "#94a3b8" }}>
                  Deep expertise in sourcing and assessing senior talent for high-stakes executive positions across multiple industries.
                </p>
              </div>
            </div>

            {/* Bento Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 auto-rows-auto">
              {/* Row 1: Banking (2 cols wide) | Technology | Consulting */}
              <div className={`col-span-2 p-7 ${cardClass} group`}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5 transition-colors" style={{ backgroundColor: "rgba(99,102,241,0.12)" }}>
                  <Landmark className="w-6 h-6" style={{ color: "#818cf8" }} />
                </div>
                <h3 className="text-lg font-bold mb-2" style={{ color: "#ffffff" }}>Banking &amp; Finance</h3>
                <p className="text-sm mb-4 leading-relaxed" style={{ color: "#64748b" }}>CFOs, Risk Directors, Investment Managers, Compliance Heads</p>
                <ul className="space-y-2 text-sm" style={{ color: "#94a3b8" }}>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: "#34d399" }} /> Regulatory compliance reviewed</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: "#34d399" }} /> Qualification validation</li>
                </ul>
              </div>

              <div className={`p-7 ${cardClass} group`}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={{ backgroundColor: "rgba(99,102,241,0.12)" }}>
                  <Monitor className="w-6 h-6" style={{ color: "#818cf8" }} />
                </div>
                <h3 className="text-lg font-bold mb-2" style={{ color: "#ffffff" }}>Technology</h3>
                <p className="text-sm mb-4 leading-relaxed" style={{ color: "#64748b" }}>CTOs, Engineering VPs, Product Directors, AI/ML Leads</p>
                <ul className="space-y-2 text-sm" style={{ color: "#94a3b8" }}>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: "#34d399" }} /> Technical assessment</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: "#34d399" }} /> Portfolio review</li>
                </ul>
              </div>

              <div className={`p-7 ${cardClass} group`}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={{ backgroundColor: "rgba(99,102,241,0.12)" }}>
                  <BarChart3 className="w-6 h-6" style={{ color: "#818cf8" }} />
                </div>
                <h3 className="text-lg font-bold mb-2" style={{ color: "#ffffff" }}>Consulting</h3>
                <p className="text-sm mb-4 leading-relaxed" style={{ color: "#64748b" }}>Partners, Managing Directors, Practice Leads, Strategy Heads</p>
                <ul className="space-y-2 text-sm" style={{ color: "#94a3b8" }}>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: "#34d399" }} /> Client track record</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: "#34d399" }} /> Revenue attribution</li>
                </ul>
              </div>

              {/* Row 2: Corporate | Medical (2 wide) | SMB */}
              <div className={`p-7 ${cardClass} group`}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={{ backgroundColor: "rgba(99,102,241,0.12)" }}>
                  <Building2 className="w-6 h-6" style={{ color: "#818cf8" }} />
                </div>
                <h3 className="text-lg font-bold mb-2" style={{ color: "#ffffff" }}>Corporate</h3>
                <p className="text-sm mb-4 leading-relaxed" style={{ color: "#64748b" }}>CEOs, COOs, CHROs, General Managers, Board Directors</p>
                <ul className="space-y-2 text-sm" style={{ color: "#94a3b8" }}>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: "#34d399" }} /> Board experience assessed</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: "#34d399" }} /> Leadership assessment</li>
                </ul>
              </div>

              <div className={`col-span-2 p-7 ${cardClass} group`}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={{ backgroundColor: "rgba(99,102,241,0.12)" }}>
                  <Stethoscope className="w-6 h-6" style={{ color: "#818cf8" }} />
                </div>
                <h3 className="text-lg font-bold mb-2" style={{ color: "#ffffff" }}>Medical &amp; Healthcare</h3>
                <p className="text-sm mb-4 leading-relaxed" style={{ color: "#64748b" }}>Clinical Directors, Hospital CEOs, Medical Officers, Health Executives</p>
                <ul className="space-y-2 text-sm" style={{ color: "#94a3b8" }}>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: "#34d399" }} /> Medical credential verification</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: "#34d399" }} /> Registration validation</li>
                </ul>
              </div>

              <div className={`p-7 ${cardClass} group`}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={{ backgroundColor: "rgba(99,102,241,0.12)" }}>
                  <Building className="w-6 h-6" style={{ color: "#818cf8" }} />
                </div>
                <h3 className="text-lg font-bold mb-2" style={{ color: "#ffffff" }}>SMB &amp; Start-ups</h3>
                <p className="text-sm mb-4 leading-relaxed" style={{ color: "#64748b" }}>Founders, CEOs, Operations Directors, Growth Leaders</p>
                <ul className="space-y-2 text-sm" style={{ color: "#94a3b8" }}>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: "#34d399" }} /> Agile &amp; growth-focused</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: "#34d399" }} /> Entrepreneurial mindset</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className={sectionSep} />

      {/* Candidate Assessment Engine */}
      <section id="capability" className="py-16 md:py-28" style={{ backgroundColor: "#05070a", position: "relative" }}>
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 55% 45% at 85% 20%, rgba(55,48,163,0.15) 0%, transparent 70%)" }} />
        <div className="container mx-auto px-4 relative">
          <div className="max-w-6xl mx-auto">

            <div className="mb-14 md:mb-20">
              <p className="uppercase tracking-widest text-xs mb-4 font-semibold" style={{ color: "#818cf8" }}>Features</p>
              <h2 className="text-2xl md:text-4xl font-bold" style={{ color: "#ffffff" }}>
                For recruiters whose clients expect more than a ranked list
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-5">
              {[
                { icon: Video, title: "Structured video responses", desc: "Candidates answer role-specific questions on video - no scheduling, no back-and-forth. Responses are recorded, transcribed, and scored automatically in under 2 minutes of submission." },
                { icon: BarChart3, title: "AI triage that surfaces what matters", desc: "Every submission is scored against your role criteria and banded Green, Amber, or Red. Your reviewer sees the priority cases first - with flagged moments and recommended focus areas highlighted." },
                { icon: FileCheck, title: "A dossier your client can read in 2 minutes", desc: "The output is a professional candidate dossier - executive summary, role fit analysis, competency ratings, key interview moments, and suggested follow-up questions for the next stage." },
              ].map(({ icon: Icon, title, desc }) => (
                <div key={title} className={`p-7 ${cardClass}`}>
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={{ backgroundColor: "rgba(99,102,241,0.14)" }}>
                    <Icon className="w-6 h-6" style={{ color: "#818cf8" }} />
                  </div>
                  <h3 className="text-xl font-bold mb-3" style={{ color: "#ffffff" }}>{title}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: "#94a3b8" }}>{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className={sectionSep} />

      {/* How It Works — Bento */}
      <section id="process" className="py-16 md:py-28" style={{ backgroundColor: "#080b12", position: "relative" }}>
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 50% 60% at 10% 60%, rgba(55,48,163,0.14) 0%, transparent 70%)" }} />
        <div className="container mx-auto px-4 relative">
          <div className="max-w-6xl mx-auto">

            <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center mb-12 md:mb-20">
              <div>
                <p className="uppercase tracking-widest text-xs mb-3 font-semibold" style={{ color: "#818cf8" }}>How It Works</p>
                <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{
                  background: "linear-gradient(to bottom, #ffffff 0%, #c7d2fe 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text"
                }}>
                  Recruitment the way it should be.
                </h2>
                <p className="text-base md:text-lg leading-relaxed" style={{ color: "#94a3b8" }}>
                  A structured process that respects candidates and empowers recruiters.
                </p>
              </div>
              <div className="relative rounded-2xl overflow-hidden" style={{
                boxShadow: "0 0 0 1px rgba(99,102,241,0.3), 0 0 60px rgba(99,102,241,0.10)",
              }}>
                <img
                  src={teamCollaborationImage}
                  alt="Vexis analytics dashboard showing pipeline velocity, placement rates and candidate outcome charts"
                  className="w-full h-64 lg:h-80 object-cover block"
                  loading="lazy"
                  decoding="async"
                  data-testid="img-team-collaboration"
                />
                <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(8,11,18,0.6) 0%, transparent 60%)" }} />
              </div>
            </div>

            {/* Bento steps grid */}
            <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
              {/* Step 1 — wide */}
              <div className={`md:col-span-3 p-7 ${cardClass}`}>
                <div className="flex items-center justify-between mb-5">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg" style={{ backgroundColor: "#6366f1", color: "#ffffff" }}>1</div>
                  <div className="flex items-center gap-2.5 px-3.5 py-2 rounded-full" style={{ backgroundColor: "rgba(59,130,246,0.1)", border: "1px solid rgba(59,130,246,0.3)" }}>
                    <img src="/images/jobadder-logo.jpg" alt="JobAdder" className="h-7 w-7 rounded-lg object-cover" style={{ boxShadow: "0 0 0 1px rgba(59,130,246,0.4)" }} />
                    <span className="text-sm font-semibold" style={{ color: "#60a5fa" }}>JobAdder</span>
                  </div>
                </div>
                <h3 className="text-xl font-bold mb-3" style={{ color: "#ffffff" }}>Send a real assessment</h3>
                <p className="text-sm leading-relaxed mb-5" style={{ color: "#94a3b8" }}>
                  One click from JobAdder sends your candidate a structured video interview. No scheduling. No back and forth. They complete it in their own time, on any device, in under 15 minutes.
                </p>
                <div className="flex flex-wrap gap-2">
                  {["Any Device","No Scheduling","Under 15 Minutes"].map(tag => (
                    <span key={tag} className="px-3 py-1.5 rounded-full text-xs font-medium" style={{ backgroundColor: "rgba(99,102,241,0.12)", color: "#a5b4fc" }}>{tag}</span>
                  ))}
                </div>
              </div>

              {/* Step 2 */}
              <div className={`md:col-span-3 p-7 ${cardClass}`}>
                <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg mb-5" style={{ backgroundColor: "#6366f1", color: "#ffffff" }}>2</div>
                <h3 className="text-xl font-bold mb-3" style={{ color: "#ffffff" }}>Let the AI do the groundwork</h3>
                <p className="text-sm leading-relaxed mb-5" style={{ color: "#94a3b8" }}>
                  Every response is transcribed and scored against your role criteria. Confidence bands. Strengths. Concerns. Delivered before you watch a single frame.
                </p>
                <div className="flex flex-wrap gap-2">
                  {["Auto Transcription","Confidence Bands","Role Criteria Scoring"].map(tag => (
                    <span key={tag} className="px-3 py-1.5 rounded-full text-xs font-medium" style={{ backgroundColor: "rgba(99,102,241,0.12)", color: "#a5b4fc" }}>{tag}</span>
                  ))}
                </div>
              </div>

              {/* Step 3 */}
              <div className={`md:col-span-2 p-7 ${cardClass}`}>
                <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg mb-5" style={{ backgroundColor: "#6366f1", color: "#ffffff" }}>3</div>
                <h3 className="text-xl font-bold mb-3" style={{ color: "#ffffff" }}>You make the call</h3>
                <p className="text-sm leading-relaxed mb-5" style={{ color: "#94a3b8" }}>
                  The AI informs. You decide. Review the transcript, watch the responses, and set your outcome. No automated rejections. No black-box decisions. Every candidate gets a fair hearing.
                </p>
                <div className="flex flex-wrap gap-2">
                  {["Human Review Required","No Automated Rejections"].map(tag => (
                    <span key={tag} className="px-3 py-1.5 rounded-full text-xs font-medium" style={{ backgroundColor: "rgba(99,102,241,0.12)", color: "#a5b4fc" }}>{tag}</span>
                  ))}
                </div>
              </div>

              {/* Step 4 */}
              <div className={`md:col-span-2 p-7 ${cardClass}`}>
                <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg mb-5" style={{ backgroundColor: "#6366f1", color: "#ffffff" }}>4</div>
                <h3 className="text-xl font-bold mb-3" style={{ color: "#ffffff" }}>Present with confidence</h3>
                <p className="text-sm leading-relaxed mb-5" style={{ color: "#94a3b8" }}>
                  A boardroom-ready dossier lands in your client's inbox automatically. Score, summary, and your professional recommendation — all in one place.
                </p>
                <div className="flex flex-wrap gap-2">
                  {["Boardroom-Ready Dossier","Auto-Delivered"].map(tag => (
                    <span key={tag} className="px-3 py-1.5 rounded-full text-xs font-medium" style={{ backgroundColor: "rgba(99,102,241,0.12)", color: "#a5b4fc" }}>{tag}</span>
                  ))}
                </div>
              </div>

              {/* Step 5 */}
              <div className={`md:col-span-2 p-7 ${cardClass}`}>
                <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg mb-5" style={{ backgroundColor: "#6366f1", color: "#ffffff" }}>5</div>
                <h3 className="text-xl font-bold mb-3" style={{ color: "#ffffff" }}>Results write back to your ATS</h3>
                <p className="text-sm leading-relaxed mb-5" style={{ color: "#94a3b8" }}>
                  Score, written recommendation, and a secure dossier link are written back to the candidate record automatically. Your client can read the full picture in under 2 minutes.
                </p>
                <div className="flex flex-wrap gap-2">
                  {["Written Back to Your ATS","2-Minute Read"].map(tag => (
                    <span key={tag} className="px-3 py-1.5 rounded-full text-xs font-medium" style={{ backgroundColor: "rgba(99,102,241,0.12)", color: "#a5b4fc" }}>{tag}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className={sectionSep} />

      {/* Compliance & Trust */}
      <section className="py-12 md:py-20" style={{ backgroundColor: "#05070a" }}>
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <p className="uppercase tracking-widest text-xs mb-3 font-semibold" style={{ color: "#818cf8" }}>Security &amp; Compliance</p>
              <h2 className="text-2xl md:text-3xl font-bold mb-3" style={{ color: "#ffffff" }}>Enterprise-Grade Compliance</h2>
              <p className="text-base" style={{ color: "#94a3b8" }}>Built for New Zealand and Australian regulatory requirements</p>
            </div>

            <div className="grid grid-cols-2 gap-4 md:gap-5 max-w-md mx-auto">
              {[
                { flag: "🇳🇿", name: "New Zealand", laws: ["Privacy Act 2020","Human Rights Act"] },
                { flag: "🇦🇺", name: "Australia", laws: ["Privacy Act 1988","Fair Work Act"] },
              ].map(({ flag, name, laws }) => (
                <div key={name} className={`p-5 md:p-6 text-center ${cardClass}`}>
                  <div className="text-2xl mb-2">{flag}</div>
                  <h4 className="font-bold text-sm md:text-base" style={{ color: "#ffffff" }}>{name}</h4>
                  {laws.map(l => <p key={l} className="text-xs mt-1" style={{ color: "#64748b" }}>{l}</p>)}
                </div>
              ))}
            </div>

            <div className="mt-12 flex flex-wrap justify-center gap-6 sm:gap-10 text-sm" style={{ color: "#64748b" }}>
              {[
                { icon: Shield, label: "SOC 2 Type II (in progress)" },
                { icon: Lock, label: "256-bit Encryption" },
                { icon: BadgeCheck, label: "Zero-Bias Certification" },
                { icon: Globe, label: "Trans-Tasman Compliance" },
              ].map(({ icon: Icon, label }) => (
                <div key={label} className="flex items-center gap-2">
                  <Icon className="w-5 h-5" style={{ color: "#818cf8" }} />
                  <span>{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className={sectionSep} />

      {/* Social Proof */}
      <section className="py-10 md:py-16" style={{ backgroundColor: "#05070a" }}>
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <p className="uppercase tracking-widest text-xs mb-4 font-semibold" style={{ color: "#818cf8" }}>Our position</p>
            <blockquote className="text-base md:text-lg leading-relaxed mb-6" style={{ color: "#e2e8f0" }}>
              "In a market where AI is replacing human judgement, Vexis puts the recruiter back in the room. We use technology to prepare you for the decision — not to make it for you."
            </blockquote>
            <p className="text-sm font-semibold" style={{ color: "#a5b4fc" }}>
              Vexis — Human First. Technology Assisted.
            </p>
          </div>
        </div>
      </section>

      <div className={sectionSep} />

      {/* Trust & Operating Standards */}
      <section className="py-12 md:py-20" style={{ backgroundColor: "#080b12" }}>
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <p className="uppercase tracking-widest text-xs mb-3 font-semibold" style={{ color: "#818cf8" }}>Credibility</p>
              <h2 className="text-2xl md:text-3xl font-bold mb-3" style={{ color: "#ffffff" }}>Built for recruiters who still believe people matter.</h2>
              <p className="text-base" style={{ color: "#94a3b8" }}>Three principles that never change, no matter how many candidates you process.</p>
            </div>

            <div className="grid md:grid-cols-3 gap-5">
              {[
                { icon: Shield, title: "Human review. Always.", desc: "No candidate is rejected by an algorithm. Every outcome requires a completed human review. It's not a feature — it's a rule." },
                { icon: BadgeCheck, title: "Not a resume filter.", desc: "Resumes are a starting point. Vexis goes further — giving every candidate the chance to show up, speak up, and stand out on their own terms." },
                { icon: Lock, title: "Your ATS. Enhanced.", desc: "Vexis lives inside your existing JobAdder workflow. One button. Candidate data syncs automatically. Results write back to the record. No new platform to learn." },
              ].map(({ icon: Icon, title, desc }) => (
                <div key={title} className={`p-7 text-center ${cardClass}`}>
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5 mx-auto" style={{ backgroundColor: "rgba(99,102,241,0.14)" }}>
                    <Icon className="w-6 h-6" style={{ color: "#818cf8" }} />
                  </div>
                  <h3 className="text-lg font-bold mb-2" style={{ color: "#ffffff" }}>{title}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: "#94a3b8" }}>{desc}</p>
                </div>
              ))}
            </div>

            <p className="text-center text-sm mt-8" style={{ color: "#475569" }}>Case studies available on request.</p>
          </div>
        </div>
      </section>

      <div className={sectionSep} />

      {/* CTA Strip */}
      <section id="enterprise" className="relative py-16 md:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" style={{ backgroundImage: `url(${ctaBannerImage})` }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(5,7,10,0.95) 0%, rgba(30,27,75,0.88) 50%, rgba(5,7,10,0.92) 100%)" }} />
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 60% 60% at 50% 50%, rgba(99,102,241,0.18) 0%, transparent 70%)" }} />
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-5 leading-tight" style={{
              background: "linear-gradient(to bottom, #ffffff 0%, #c7d2fe 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text"
            }}>
              Your assessment standard. Built into the platform you already use.
            </h2>
            <p className="text-lg md:text-xl mb-10 max-w-2xl mx-auto leading-relaxed" style={{ color: "#94a3b8" }}>
              Vexis connects to your ATS via OAuth. Candidates complete a structured video assessment in under 15 minutes. A human reviews every submission. A boardroom-ready dossier lands in your candidate record automatically.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link href="/contact">
                <Button size="lg" className="text-base sm:text-lg px-8 h-14 w-full sm:w-auto font-semibold"
                  style={{ backgroundColor: "#6366f1", color: "#ffffff" }}>
                  See How It Works
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link href="/contact">
                <Button size="lg" variant="outline" className="text-base sm:text-lg px-8 h-14 w-full sm:w-auto"
                  style={{ borderColor: "rgba(255,255,255,0.25)", color: "#ffffff", backgroundColor: "transparent" }}>
                  Contact
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ backgroundColor: "#020408", borderTop: "1px solid rgba(99,102,241,0.15)" }} className="text-white py-12 md:py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 mb-10">
            <div className="col-span-2">
              <div className="mb-5">
                <img src={vexisLogo} alt="VEXIS Talent Agency" className="h-12 md:h-14 w-auto" />
              </div>
              <p className="text-sm max-w-xs leading-relaxed" style={{ color: "#475569" }}>
                Vexis is a hybrid recruitment assessment platform built for agencies and talent teams who believe the best hire is always a human decision.
              </p>
            </div>

            <div>
              <h4 className="font-bold mb-4" style={{ color: "#ffffff" }}>Platform</h4>
              <ul className="space-y-2 text-sm" style={{ color: "#475569" }}>
                <li><a href="#process" className="hover:text-white transition-colors">How It Works</a></li>
                <li><a href="#capability" className="hover:text-white transition-colors">Features</a></li>
                <li><Link href="/pricing" className="hover:text-white transition-colors">Pricing</Link></li>
                <li><Link href="/start" className="hover:text-white transition-colors">Start an Agency</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold mb-4" style={{ color: "#ffffff" }}>Company</h4>
              <ul className="space-y-2 text-sm" style={{ color: "#475569" }}>
                <li><Link href="/about" className="hover:text-white transition-colors">About Vexis</Link></li>
                <li><Link href="/contact" className="hover:text-white transition-colors">Contact</Link></li>
                <li><Link href="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link href="/terms" className="hover:text-white transition-colors">Terms</Link></li>
                <li><Link href="/security" className="hover:text-white transition-colors">Security</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold mb-4" style={{ color: "#ffffff" }}>Get in Touch</h4>
              <ul className="space-y-3 text-sm" style={{ color: "#475569" }}>
                <li>
                  <p className="text-xs uppercase tracking-wider mb-0.5" style={{ color: "#374151" }}>Phone</p>
                  <a href="tel:+64212458827" className="hover:text-white transition-colors">+64 212 458 827</a>
                </li>
                <li>
                  <p className="text-xs uppercase tracking-wider mb-0.5" style={{ color: "#374151" }}>General</p>
                  <a href="mailto:hello@vexistalentagency.com" className="hover:text-white transition-colors">hello@vexistalentagency.com</a>
                </li>
                <li>
                  <p className="text-xs uppercase tracking-wider mb-0.5" style={{ color: "#374151" }}>Partnerships &amp; API</p>
                  <a href="mailto:api@vexistalentagency.com" className="hover:text-white transition-colors">api@vexistalentagency.com</a>
                </li>
                <li>
                  <p className="text-xs uppercase tracking-wider mb-0.5" style={{ color: "#374151" }}>Privacy</p>
                  <a href="mailto:privacy@vexistalentagency.com" className="hover:text-white transition-colors">privacy@vexistalentagency.com</a>
                </li>
              </ul>
            </div>
          </div>

          <div style={{ borderTop: "1px solid rgba(99,102,241,0.12)" }} className="pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-sm" style={{ color: "#374151" }}>
                &copy; 2026 Vexis. Human First. Technology Assisted.
              </p>
            </div>
            <p className="text-xs text-center mt-6" style={{ color: "#1f2937" }}>
              Vexis Talent Agency is a trading name of LHA Holdings Ltd.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
